import { useCallback, useEffect, useRef, useState } from 'react';
import { useTravelText } from '../../app/TravelLocaleContext';
import { formatDateTimeValue } from '../../i18n/travelFormat';
import { publicTimelineTypeLabelKey } from './publicJourneyPresentation';
import PublicJourneyStoryMediaImage from './PublicJourneyStoryMediaImage';
import PublicJourneyStoryMediaViewer from './PublicJourneyStoryMediaViewer';

const P = 'travel.publicJourney.story';
const AUTHOR_ROLES = new Set(['OWNER', 'COMPANION']);

const cardKeyOf = (card, index) => String(card.cardId || `story-card-${index}`);

/**
 * 公開旅遊故事（文字＋授權圖片）。內容一律以 React 文字節點呈現，不插入 HTML；
 * 不顯示作者姓名、objectKey 或 Day／活動跳轉。
 */
export default function PublicJourneyStoryView({
  story,
  publicationId,
  viewerKey = 'anon',
  onRequireAuth,
  onShowOverview,
}) {
  const { t, intlLocale } = useTravelText();
  const sectionRef = useRef(null);
  const triggerRef = useRef(null);
  // viewer：以 cardKey＋mediaId 追蹤；開啟時記下 publication／viewerKey，任一改變即不再渲染（立即關閉）
  const [viewer, setViewer] = useState(null);
  const empty = !story?.available || story.cards.length === 0;
  const cards = empty ? [] : story.cards;
  const viewerCardIndex = viewer ? cards.findIndex((card, index) => cardKeyOf(card, index) === viewer.cardKey) : -1;
  const viewerCard = viewerCardIndex >= 0 ? cards[viewerCardIndex] : null;
  const viewerMedia = Array.isArray(viewerCard?.media) ? viewerCard.media : [];
  const viewerOpen = Boolean(viewer)
    && viewer.publicationId === publicationId
    && viewer.viewerKey === viewerKey
    && viewerMedia.some((item) => item.mediaId === viewer.mediaId);

  useEffect(() => {
    // 清單重排保持同一張；該圖／該卡移除或身分、publication 改變 → 清除狀態（不切換到其他圖片）
    if (viewer && !viewerOpen) setViewer(null);
  }, [viewer, viewerOpen]);

  const closeViewer = useCallback(() => setViewer(null), []);
  const navigateViewer = useCallback((nextMediaId) => {
    setViewer((current) => (current ? { ...current, mediaId: nextMediaId } : current));
  }, []);
  // 返回焦點：開啟它的縮圖（仍存在時）→ 同卡仍在的開啟按鈕 → 本區任一開啟按鈕 → 故事檢視切換鈕
  const focusFallback = useCallback(() => {
    if (triggerRef.current?.isConnected) {
      triggerRef.current.focus();
      return;
    }
    const root = sectionRef.current;
    const cardKey = viewer?.cardKey;
    const cardNode = root
      ? Array.from(root.querySelectorAll('[data-story-card-key]')).find((node) => node.dataset.storyCardKey === cardKey)
      : null;
    const target = cardNode?.querySelector('.journey-story__media-open')
      || root?.querySelector('.journey-story__media-open')
      || document.querySelector('[data-testid="public-journey-view-story"]');
    target?.focus?.();
  }, [viewer?.cardKey]);

  return (
    <section
      ref={sectionRef}
      id="public-journey-story-panel"
      className="journey-story"
      aria-labelledby="journey-story-title"
      data-testid="public-journey-story"
    >
      <div className="journey-section-heading">
        <div>
          <p className="journey-eyebrow">{t(`${P}.eyebrow`)}</p>
          <h2 id="journey-story-title">{t(`${P}.title`)}</h2>
        </div>
      </div>
      {empty ? (
        <div
          className="timeline-state"
          data-testid={story?.available ? 'public-journey-story-empty' : 'public-journey-story-unavailable'}
        >
          <h3>{t(story?.available ? `${P}.emptyTitle` : `${P}.unavailableTitle`)}</h3>
          <p>
            {t(story?.available ? `${P}.emptyBody` : `${P}.unavailableBody`)}
          </p>
          <button
            type="button"
            className="journey-story__overview-link"
            data-testid="public-journey-story-to-overview"
            onClick={onShowOverview}
          >
            {t(`${P}.toOverview`)}
          </button>
        </div>
      ) : (
        <ol className="journey-story__list">
          {story.cards.map((card, index) => {
            const occurredAt = formatDateTimeValue(card.occurredAt, intlLocale);
            const media = Array.isArray(card.media) ? card.media : [];
            const cardKey = cardKeyOf(card, index);
            return (
              <li
                key={card.cardId || `story-card-${index}`}
                className="journey-story__card"
                data-testid="public-journey-story-card"
                data-story-card-key={cardKey}
              >
                <article>
                  <div className="timeline-card__meta">
                    <span>{t(publicTimelineTypeLabelKey({ type: 'MEMORY', memoryType: card.memoryType }))}</span>
                    {AUTHOR_ROLES.has(card.authorRole) ? <span>{t(`${P}.role.${card.authorRole}`)}</span> : null}
                  </div>
                  {card.title ? <h3>{card.title}</h3> : null}
                  {card.content ? <p className="journey-story__content">{card.content}</p> : null}
                  {media.length > 0 ? (
                    <div className="journey-story__media-list" data-testid="public-journey-story-media-list">
                      {media.map((item, mediaIndex) => (
                        <PublicJourneyStoryMediaImage
                          key={item.mediaId}
                          publicationId={publicationId}
                          mediaId={item.mediaId}
                          index={mediaIndex + 1}
                          total={media.length}
                          viewerKey={viewerKey}
                          onRequireAuth={onRequireAuth}
                          onOpen={(event) => {
                            triggerRef.current = event?.currentTarget || null;
                            setViewer({ cardKey, mediaId: item.mediaId, publicationId, viewerKey });
                          }}
                        />
                      ))}
                    </div>
                  ) : null}
                  {occurredAt ? <time dateTime={card.occurredAt}>{occurredAt}</time> : null}
                </article>
              </li>
            );
          })}
        </ol>
      )}
      {viewerOpen ? (
        <PublicJourneyStoryMediaViewer
          publicationId={publicationId}
          media={viewerMedia}
          mediaId={viewer.mediaId}
          cardTitle={viewerCard.title}
          onNavigate={navigateViewer}
          onClose={closeViewer}
          onRequireAuth={onRequireAuth}
          returnFocusFallback={focusFallback}
        />
      ) : null}
    </section>
  );
}
